import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthenticationService } from "./AuthenticationService";

export type AuthenticationContextType = {
  token?: string;
  email?: string;
  username?: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (redirectUri: string) => Promise<void>;
  logout: () => Promise<void>;
  finishSignIn: () => Promise<string | undefined>;
  clearAuthState: () => Promise<void>;
  isCallbackInProgress: () => boolean;
};

const AuthenticationContext = createContext<AuthenticationContextType>({
  token: "",
  email: "",
  username: "",
  isAuthenticated: false,
  isLoading: true,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  finishSignIn: () => Promise.resolve(undefined),
  clearAuthState: () => Promise.resolve(),
  isCallbackInProgress: () => false,
});

export const useAuthentication = () => useContext<AuthenticationContextType>(AuthenticationContext);

type AuthenticationProviderState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string;
  email: string | undefined;
  username: string | undefined;
};

export const AuthenticationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [authContext, setAuthContext] = useState<AuthenticationProviderState>({
    isAuthenticated: false,
    isLoading: true,
    token: "",
    email: "",
    username: "",
  });

  useEffect(() => {
    // Check if user is already authenticated on mount
    AuthenticationService.Instance.getUser().then((user) => {
      const isAuthed = user.token !== undefined;
      setAuthContext({
        isAuthenticated: isAuthed,
        isLoading: false,
        token: user.token ?? "",
        email: user.email,
        username: user.username,
      });
    }).catch((error) => {
      console.error("[AuthContext] Error initializing auth state:", error);
      setAuthContext(prev => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
      }));
    });

    // Setup callback for token renewal events
    AuthenticationService.Instance.addRenewCallback((success, token, email, username) => {
      if (success && token) {
        setAuthContext(prev => ({ 
          ...prev,
          isAuthenticated: true, 
          token, 
          email, 
          username 
        }));
      } else {
        setAuthContext(prev => ({ 
          ...prev,
          isAuthenticated: false, 
          token: "", 
          email: "", 
          username: "" 
        }));
      }
    });

    return () => {
      AuthenticationService.Instance.removeRenewCallback();
    };
  }, []);

  const login = React.useCallback(
    (redirectUri: string) => {
      return AuthenticationService.Instance.login(redirectUri);
    },
    []
  );

  const logout = React.useCallback(
    () => {
      return AuthenticationService.Instance.logout();
    },
    []
  );

  const finishSignIn = React.useCallback(
    () => AuthenticationService.Instance.acceptCallback(), 
    []
  );
  
  const clearAuthState = React.useCallback(
    () => AuthenticationService.Instance.clearAuthenticationState(), 
    []
  );
  
  const isCallbackInProgress = React.useCallback(
    () => AuthenticationService.Instance.isCallbackInProgress(), 
    []
  );

  const contextValue = React.useMemo(
    () => ({
      isAuthenticated: authContext.isAuthenticated,
      isLoading: authContext.isLoading,
      token: authContext.token,
      email: authContext.email,
      username: authContext.username,
      login,
      logout,
      finishSignIn,
      clearAuthState,
      isCallbackInProgress,
    }),
    [
      authContext.isAuthenticated,
      authContext.isLoading,
      authContext.token,
      authContext.email,
      authContext.username,
      login,
      logout,
      finishSignIn,
      clearAuthState,
      isCallbackInProgress,
    ]
  );

  return (
    <AuthenticationContext.Provider value={contextValue}>
      {children}
    </AuthenticationContext.Provider>
  );
};
